import React, { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Menu, Search, Bell } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface MobileHeaderProps {
  title?: string;
  showBackButton?: boolean;
  showMenuButton?: boolean;
  showSearchButton?: boolean;
  showNotificationButton?: boolean;
  className?: string;
  onOpenMenu?: () => void;
  onOpenSearch?: () => void;
  onOpenNotifications?: () => void;
  avatarUrl?: string;
  rightContent?: ReactNode;
  centerContent?: ReactNode;
  variant?: 'default' | 'transparent' | 'primary' | 'welcome';
  userName?: string;
}

export const MobileHeader: React.FC<MobileHeaderProps> = ({
  title,
  showBackButton = false,
  showMenuButton = false,
  showSearchButton = true,
  showNotificationButton = true,
  className,
  onOpenMenu,
  onOpenSearch,
  onOpenNotifications,
  avatarUrl,
  rightContent,
  centerContent,
  variant = 'default',
  userName,
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  const renderLeftSide = () => {
    if (showBackButton) {
      return (
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={handleBack}
          className="h-9 w-9 text-foreground"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
      );
    }

    if (showMenuButton) {
      return (
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onOpenMenu}
          className="h-9 w-9 text-foreground"
        >
          <Menu className="h-5 w-5" />
        </Button>
      );
    }

    return null;
  };

  const renderRightSide = () => {
    if (rightContent) {
      return rightContent;
    }

    return (
      <div className="flex items-center gap-1">
        {showSearchButton && (
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onOpenSearch}
            className="h-9 w-9 text-foreground"
          >
            <Search className="h-5 w-5" />
          </Button>
        )}
        
        {showNotificationButton && (
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onOpenNotifications}
            className="h-9 w-9 text-foreground"
          >
            <Bell className="h-5 w-5" />
          </Button>
        )}
        
        {avatarUrl && (
          <div className="w-9 h-9 rounded-full overflow-hidden border border-muted">
            <img 
              src={avatarUrl} 
              alt="User avatar" 
              className="w-full h-full object-cover"
            />
          </div>
        )}
      </div>
    );
  };

  const renderCenter = () => {
    if (centerContent) {
      return centerContent;
    }

    if (variant === 'welcome' && userName) {
      return (
        <div className="flex flex-col items-start">
          <span className="text-sm text-muted-foreground">Xin chào,</span>
          <span className="text-lg font-medium flex items-center">
            {userName} <span className="ml-1">👋</span>
          </span>
        </div>
      );
    }

    return (
      <h1 className="text-lg font-medium truncate max-w-[200px]">
        {title}
      </h1>
    );
  };

  const headerClasses = {
    'default': 'bg-background text-foreground',
    'transparent': 'bg-transparent text-foreground',
    'primary': 'bg-primary text-primary-foreground',
    'welcome': 'bg-background text-foreground',
  };

  return (
    <div className={cn(
      "w-full px-4 py-3 flex items-center justify-between",
      headerClasses[variant],
      className
    )}>
      <div className="w-20 flex justify-start">
        {renderLeftSide()}
      </div>
      
      <div className="flex-1 flex justify-center">
        {renderCenter()}
      </div>
      
      <div className="w-20 flex justify-end">
        {renderRightSide()}
      </div>
    </div>
  );
};

export default MobileHeader; 